import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  useTheme,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Print as PrintIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../../../layouts/BranchContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/* ── Shared axios instance with token interceptor ────────────────────────── */
const api = axios.create({ baseURL: `${API_BASE_URL}/api` });

const getAccessToken = () => {
  const keys = ['accessToken', 'access_token', 'token'];
  for (const key of keys) {
    const local = localStorage.getItem(key);
    if (local) return local;
    const session = sessionStorage.getItem(key);
    if (session) return session;
  }
  return null;
};

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* Local (not UTC) today as YYYY-MM-DD — avoids the "yesterday" bug in IST */
const todayLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split('T')[0];
};

/* ═══════════════════════════════════════════════════════════════════════════
   CERTIFICATE RENDERER
   Copied 1:1 from the AddCertificate designer, so the issued certificate is
   drawn by exactly the same code as the designer's preview.
═══════════════════════════════════════════════════════════════════════════ */
const MM_TO_PX = 96 / 25.4;

const PAGE_SIZES = {
  A4: { label: 'A4', long: 297, short: 210 },
  A3: { label: 'A3', long: 420, short: 297 },
  A5: { label: 'A5', long: 210, short: 148 },
  Letter: { label: 'US Letter', long: 279.4, short: 215.9 },
  Legal: { label: 'US Legal', long: 355.6, short: 215.9 },
};

const getCanvasSize = (pageSize, orientation) => {
  const p = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;
  const wMm = orientation === 'portrait' ? p.short : p.long;
  const hMm = orientation === 'portrait' ? p.long : p.short;
  return { wMm, hMm, w: Math.round(wMm * MM_TO_PX), h: Math.round(hMm * MM_TO_PX) };
};

const SAMPLE = {
  student_name: '',
  course_name: '',
  completion_date: '',
  batch_name: '',
};

const fillPlaceholders = (text, sample = SAMPLE) =>
  String(text || '')
    .replace(/\{\{student_name\}\}/g, sample.student_name)
    .replace(/\{\{course_name\}\}/g, sample.course_name)
    .replace(/\{\{completion_date\}\}/g, sample.completion_date)
    .replace(/\{\{batch_name\}\}/g, sample.batch_name);

const getBorderCss = (style, color) => {
  switch (style) {
    case 'double': return `6px double ${color}`;
    case 'decorative': return `10px groove ${color}`;
    case 'simple': return `3px solid ${color}`;
    default: return 'none';
  }
};

const ScaledCanvas = ({ canvasW, canvasH, scale = 1, fit = false, children }) => {
  const wrapRef = React.useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!fit || !wrapRef.current) return undefined;
    const el = wrapRef.current;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  const s = fit ? (width ? width / canvasW : 0.5) : scale;

  return (
    <Box
      ref={wrapRef}
      sx={{
        width: fit ? '100%' : canvasW * s,
        height: canvasH * s,
        position: 'relative',
        overflow: 'hidden',
        mx: 'auto',
        boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: canvasW,
          height: canvasH,
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `scale(${s})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const CertificateCanvas = ({
  form,
  canvasW,
  canvasH,
  logoSrc,
  sigSrc,
  stampSrc,
  bgSrc,
  sample = SAMPLE,
  overrides = {},
  showGuides = false,
  showHints = true,
}) => {
  const useImage = form.background_type === 'image';
  const fit = form.background_fit || 'cover';
  const zoom = Number(form.background_zoom ?? 100) / 100;
  const px = Number(form.background_pos_x ?? 50);
  const py = Number(form.background_pos_y ?? 50);
  const opacity = Number(form.background_opacity ?? 100) / 100;

  const bgLayerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("${bgSrc}")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: `${px}% ${py}%`,
    opacity,
  };
  switch (fit) {
    case 'contain': bgLayerStyle.backgroundSize = 'contain'; break;
    case 'stretch': bgLayerStyle.backgroundSize = '100% 100%'; break;
    case 'original': bgLayerStyle.backgroundSize = 'auto'; break;
    case 'tile':
      bgLayerStyle.backgroundSize = `${Math.round((canvasW / 3) * zoom)}px auto`;
      bgLayerStyle.backgroundRepeat = 'repeat';
      break;
    default: bgLayerStyle.backgroundSize = 'cover';
  }
  if (fit !== 'tile') {
    bgLayerStyle.transform = `scale(${zoom})`;
    bgLayerStyle.transformOrigin = `${px}% ${py}%`;
  }

  const logoJustify =
    form.logo_position === 'top_left' ? 'flex-start' : form.logo_position === 'top_right' ? 'flex-end' : 'center';

  const stampPos = form.stamp_position;
  const sigPos = stampPos === 'center' ? 'right' : 'center';

  const instituteName = overrides.institute_name || form.institute_name;
  const titleText = overrides.certificate_title || form.certificate_title || 'Certificate';
  const bodyText = overrides.body_text || form.body_text;

  const labelSx = { fontSize: 16, color: form.body_color, mt: 0.5, lineHeight: 1.3 };

  const signature = sigSrc ? (
    <Box sx={{ textAlign: 'center' }}>
      <Box component="img" src={sigSrc} alt="Signature" sx={{ height: 64, objectFit: 'contain', display: 'block', mx: 'auto', mb: 0.5 }} />
      <Box sx={{ width: 190, borderTop: `2px solid ${form.body_color}`, mx: 'auto' }} />
      {form.signature_label && <Typography sx={labelSx}>{form.signature_label}</Typography>}
    </Box>
  ) : form.signature_label ? (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ width: 190, borderTop: `2px solid ${form.body_color}`, mx: 'auto' }} />
      <Typography sx={labelSx}>{form.signature_label}</Typography>
    </Box>
  ) : showHints ? (
    <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }}>✍ signature</Typography>
  ) : null;

  const stamp = stampSrc ? (
    <Box component="img" src={stampSrc} alt="Stamp" sx={{ height: 96, objectFit: 'contain' }} />
  ) : showHints ? (
    <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }}>🔩 stamp</Typography>
  ) : null;

  const footerCell = (pos) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', minHeight: 100 }}>
      {stampPos === pos && stamp}
      {sigPos === pos && signature}
    </Box>
  );

  return (
    <Box
      sx={{
        width: canvasW,
        height: canvasH,
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: form.background_color,
        color: form.body_color,
        textAlign: 'center',
      }}
    >
      {useImage && bgSrc && <div style={bgLayerStyle} />}

      {form.border_style !== 'none' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 22,
            border: getBorderCss(form.border_style, form.border_color),
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      )}

      {showHints && useImage && !bgSrc && (
        <Box
          sx={{
            position: 'absolute', top: 40, right: 40, zIndex: 6, px: 2, py: 0.5, borderRadius: 2,
            backgroundColor: 'rgba(211,47,47,0.9)', color: '#fff', fontSize: 16, fontWeight: 600,
          }}
        >
          ⚠ No background image uploaded
        </Box>
      )}

      <Box
        sx={{
          position: 'relative', zIndex: 3, height: '100%', boxSizing: 'border-box',
          p: '58px 70px', display: 'flex', flexDirection: 'column',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', justifyContent: logoJustify, alignItems: 'center', minHeight: 88, mb: 1 }}>
            {logoSrc ? (
              <Box component="img" src={logoSrc} alt="Logo" sx={{ height: 84, objectFit: 'contain' }} />
            ) : showHints ? (
              <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }}>🖼 drop logo</Typography>
            ) : null}
          </Box>

          {instituteName && (
            <Typography
              sx={{
                fontSize: form.institute_name_font_size,
                fontWeight: form.institute_name_bold ? 'bold' : 'normal',
                fontStyle: form.institute_name_italic ? 'italic' : 'normal',
                color: form.institute_name_color,
                textAlign: form.institute_name_alignment,
                lineHeight: 1.25,
                mb: 1.5,
              }}
            >
              {instituteName}
            </Typography>
          )}

          <Typography
            sx={{
              fontSize: form.title_font_size,
              fontWeight: form.title_bold ? 'bold' : 'normal',
              fontStyle: form.title_italic ? 'italic' : 'normal',
              color: form.title_color,
              textAlign: form.title_alignment,
              lineHeight: 1.2,
            }}
          >
            {titleText}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 4 }}>
          <Typography
            sx={{
              fontSize: form.body_font_size, color: form.body_color, lineHeight: 1.7,
              whiteSpace: 'pre-line', textAlign: 'center',
            }}
          >
            {fillPlaceholders(bodyText, sample)}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'end', gap: 2 }}>
          {footerCell('left')}
          {footerCell('center')}
          {footerCell('right')}
        </Box>
      </Box>

      {showGuides && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 8, pointerEvents: 'none' }}>
          <Box sx={{ position: 'absolute', inset: 48, border: '1px dashed rgba(233,30,99,0.7)' }} />
          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, borderLeft: '1px dashed rgba(33,150,243,0.7)' }} />
          <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed rgba(33,150,243,0.7)' }} />
        </Box>
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CERTIFICATE PRINT VIEW (replaces the old external CertificatePrintView)
   - Loads issued certificate + its template
   - Renders with CertificateCanvas (same as designer preview)
   - Print / Download PDF -> browser print dialog at exact page size
═══════════════════════════════════════════════════════════════════════════ */
const DEFAULT_BODY =
  'This is to certify that {{student_name}} has successfully completed the {{course_name}} on {{completion_date}} under batch {{batch_name}}.';

const templateToForm = (d) => ({
  page_size: PAGE_SIZES[d.page_size] ? d.page_size : 'A4',
  orientation: d.orientation === 'portrait' ? 'portrait' : 'landscape',
  logo_position: d.logo_position ?? 'top_left',
  institute_name: d.institute_name ?? '',
  institute_name_font_size: Number(d.institute_name_font_size ?? 24),
  institute_name_bold: d.institute_name_bold ?? true,
  institute_name_italic: d.institute_name_italic ?? false,
  institute_name_color: d.institute_name_color ?? '#003366',
  institute_name_alignment: d.institute_name_alignment ?? 'center',
  certificate_title: d.certificate_title ?? 'Certificate of Completion',
  title_font_size: Number(d.title_font_size ?? 36),
  title_bold: d.title_bold ?? true,
  title_italic: d.title_italic ?? false,
  title_color: d.title_color ?? '#000000',
  title_alignment: d.title_alignment ?? 'center',
  body_text: d.body_text ?? DEFAULT_BODY,
  body_font_size: Number(d.body_font_size ?? 18),
  body_color: d.body_color ?? '#000000',
  signature_label: d.signature_label ?? '',
  stamp_position: d.stamp_position ?? 'right',
  border_style: d.border_style ?? 'simple',
  border_color: d.border_color ?? '#000000',
  background_type: d.background_type ?? 'color',
  background_color: d.background_color ?? '#FFFFFF',
  background_fit: d.background_fit ?? 'cover',
  background_zoom: Number(d.background_zoom ?? 100),
  background_pos_x: Number(d.background_pos_x ?? 50),
  background_pos_y: Number(d.background_pos_y ?? 50),
  background_opacity: Number(d.background_opacity ?? 100),
});

const absUrl = (u) => {
  if (!u) return null;
  if (/^(https?:|data:|blob:)/.test(u)) return u;
  return `${API_BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`;
};

const fmtDateDMY = (s) => {
  if (!s) return '';
  const p = String(s).split('T')[0].split('-');
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : String(s);
};

const CertificatePrintView = ({ certificateId, onClose }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [sample, setSample] = useState(SAMPLE);
  const [imgs, setImgs] = useState({ logo: null, sig: null, stamp: null, bg: null });
  const [certNo, setCertNo] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const printRes = await api.get(`/admission/issue/print/${certificateId}/`);
        const raw = printRes.data || {};
        const d = raw.certificate || raw.data || raw;

        const tplField = d.template;
        const templateId =
          d.template_id ?? (tplField && typeof tplField === 'object' ? tplField.id : tplField);

        let tpl = tplField && typeof tplField === 'object' && tplField.body_text !== undefined ? tplField : null;
        if (!tpl) {
          if (!templateId) throw new Error('Template not found for this certificate');
          const tplRes = await api.get(`/admission/certificate-template/${templateId}/`);
          tpl = tplRes.data;
        }
        if (cancelled) return;

        setForm(templateToForm(tpl));
        setSample({
          student_name: d.student_name || d.student?.student_name || d.student?.name || '',
          course_name: d.course_name || d.course?.course_name || d.course?.name || '',
          batch_name: d.batch_name || d.batch?.batch_name || d.batch?.name || '',
          completion_date: fmtDateDMY(d.completion_date || d.issue_date),
        });
        setCertNo(d.certificate_no || String(certificateId));
        setImgs({
          logo: absUrl(tpl.institute_logo_url || tpl.institute_logo),
          sig: absUrl(tpl.signature_image_url || tpl.signature_image),
          stamp: absUrl(tpl.stamp_image_url || tpl.stamp_image),
          bg: absUrl(tpl.background_image_url || tpl.background_image),
        });
      } catch (e) {
        console.error('Certificate load error:', e);
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load certificate');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (certificateId) load();
    return () => { cancelled = true; };
  }, [certificateId]);

  const canvas = form ? getCanvasSize(form.page_size, form.orientation) : null;

  // Wait for images so they are present in the print output
  const waitForImages = () =>
    Promise.all(
      [imgs.logo, imgs.sig, imgs.stamp, imgs.bg].filter(Boolean).map(
        (src) =>
          new Promise((resolve) => {
            const im = new Image();
            im.onload = resolve;
            im.onerror = resolve;
            im.src = src;
          })
      )
    );

  const handlePrint = async () => {
    await waitForImages();
    const prevTitle = document.title;
    document.title = `Certificate_${certNo}`; // suggested PDF file name
    const restore = () => {
      document.title = prevTitle;
      window.removeEventListener('afterprint', restore);
    };
    window.addEventListener('afterprint', restore);
    window.print();
  };

  const renderCert = (extra = {}) => (
    <CertificateCanvas
      form={form}
      canvasW={canvas.w}
      canvasH={canvas.h}
      logoSrc={imgs.logo}
      sigSrc={imgs.sig}
      stampSrc={imgs.stamp}
      bgSrc={imgs.bg}
      sample={sample}
      showHints={false}
      {...extra}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ backgroundColor: theme.palette.primary.main, p: '12px 16px', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', flexGrow: 1 }}>
          Certificate {certNo ? `· ${certNo}` : ''}
        </Typography>
        <Button
          variant="contained"
          color="inherit"
          size="small"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading || !!error}
          sx={{ color: theme.palette.primary.main, backgroundColor: '#fff' }}
        >
          Print
        </Button>
        <Tooltip title='In the print dialog choose "Save as PDF"'>
          <span>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handlePrint}
              disabled={loading || !!error}
            >
              Download PDF
            </Button>
          </span>
        </Tooltip>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3, backgroundColor: theme.palette.action.hover }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading certificate…</Typography>
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>{error}</Typography>
        )}
        {!loading && !error && form && (
          <ScaledCanvas canvasW={canvas.w} canvasH={canvas.h} fit>
            {renderCert()}
          </ScaledCanvas>
        )}
      </Box>

      {/* Print-only, unscaled copy (portal to <body>). Hidden on screen. */}
      {!loading && !error && form &&
        createPortal(
          <>
            <style>{`
              #cert-print-root { display: none; }
              @page { size: ${canvas.wMm}mm ${canvas.hMm}mm; margin: 0; }
              @media print {
                html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
                body > *:not(#cert-print-root) { display: none !important; }
                #cert-print-root {
                  display: block !important;
                  width: ${canvas.wMm}mm;
                  height: ${canvas.hMm}mm;
                  overflow: hidden;
                  page-break-after: avoid;
                  page-break-inside: avoid;
                }
                #cert-print-root * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
            `}</style>
            <div id="cert-print-root">{renderCert()}</div>
          </>,
          document.body
        )}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
const CertificateIssue = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { selectedBranchId } = useBranch();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [showAddCertificateModal, setShowAddCertificateModal] = useState(false);
  const [showEditCertificateModal, setShowEditCertificateModal] = useState(false);
  const [showIssueSuccessModal, setShowIssueSuccessModal] = useState(false);
  const [issueSuccessData, setIssueSuccessData] = useState(null);

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printCertificateId, setPrintCertificateId] = useState(null);

  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [approvedStudents, setApprovedStudents] = useState([]);
  const [selectedCertificateId, setSelectedCertificateId] = useState(null);

  const [organizationId, setOrganizationId] = useState('');

  useEffect(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || 'null');
      const orgId = user?.organization_id || user?.organization?.id || user?.organization || '';
      setOrganizationId(orgId ? String(orgId) : '');
    } catch (e) {
      setOrganizationId('');
    }
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleAuthError = (error) => {
    if (error.response?.status === 401) {
      ['accessToken', 'access_token', 'token'].forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
      navigate('/login');
    }
  };

  const fetchData = async (branchId = '') => {
    try {
      const params = {};
      if (organizationId) params.organization = Number(organizationId);
      if (branchId) params.branch = Number(branchId);

      const certificatesResponse = await api.get('/admission/issue/list/', { params });

      const flattenedCertificates = [];
      const responseData = certificatesResponse.data;
      const results = Array.isArray(responseData)
        ? responseData
        : (responseData?.results || responseData?.data || []);

      results.forEach((student) => {
        if (student.certificates && student.certificates.length > 0) {
          student.certificates.forEach((cert) => {
            flattenedCertificates.push({
              id: cert.certificate_id,
              certificate_id: cert.certificate_id,
              certificate_no: cert.certificate_no,
              student_name: student.student_name,
              admission_id: student.admission_id,
              admission_code: student.admission_code,
              email: student.email,
              mobile_no: student.mobile_no,
              organization: student.organization,
              branch: student.branch,
              total_fee: student.total_fee,
              pending_fee: student.pending_fee,
              course_id: cert.course_id,
              course_name: cert.course_name,
              batch_id: cert.batch_id,
              batch_name: cert.batch_name,
              template_id: cert.template_id,
              template_name: cert.template_name,
              issue_date: cert.issue_date,
              expiry_date: cert.expiry_date,
              pdf_url: cert.pdf_url || null,
            });
          });
        }
      });

      setCertificates(flattenedCertificates);
      setFilteredData(flattenedCertificates);

      const studentsResponse = await api.get('/admission/issue/pending-certificates/', { params });
      const studentsData = studentsResponse.data;
      setApprovedStudents(
        Array.isArray(studentsData)
          ? studentsData
          : (studentsData?.results || studentsData?.data || [])
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to fetch data', 'error');
      handleAuthError(error);
    }
  };

  useEffect(() => { fetchData(selectedBranchId); }, [selectedBranchId, organizationId, navigate]);

  const handleAddCertificate = async (studentId, certificatesData) => {
    try {
      const response = await api.post('/admission/issue/save/', {
        student: studentId,
        issue_date: certificatesData.issue_date,
        expiry_date: certificatesData.expiry_date,
        certificates: certificatesData.certificates,
      });

      if (response.status === 201 || response.status === 200) {
        fetchData(selectedBranchId);
        showSnackbar('Certificate(s) issued successfully!');
        if (response.data) {
          setIssueSuccessData(response.data);
          setShowIssueSuccessModal(true);
        }
      }
    } catch (error) {
      console.error('Error issuing certificate:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to issue certificate',
        'error'
      );
      handleAuthError(error);
    }
  };

  // Print AND download both open the preview modal, which renders the
  // certificate with the same code as the designer preview.
  const openCertificate = (certificateId) => {
    setPrintCertificateId(certificateId);
    setShowPrintModal(true);
  };

  const handlePrint = (certificate) => openCertificate(certificate.id);

  const handleDeleteCertificate = async (certificateId) => {
    try {
      const response = await api.delete(`/admission/issue/delete/${certificateId}/`);

      if (response.status === 200 || response.status === 204) {
        fetchData(selectedBranchId);
        showSnackbar('Certificate deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to delete certificate',
        'error'
      );
      handleAuthError(error);
    }
  };

  const handleEdit = (certificate) => {
    setSelectedCertificateId(certificate.id);
    setShowEditCertificateModal(true);
  };

  const handleDelete = (certificate) => {
    setSelectedCertificate(certificate);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedCertificate(null);
  };

  const handleConfirmDelete = () => {
    handleDeleteCertificate(selectedCertificate.id);
    setShowDeleteModal(false);
    setSelectedCertificate(null);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSaveCertificate = (studentId, certificatesData) => {
    handleAddCertificate(studentId, certificatesData);
    setShowAddCertificateModal(false);
  };

  useEffect(() => {
    setFilteredData(
      certificates.filter(
        (certificate) =>
          (certificate.student_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (certificate.template_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (certificate.course_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setPage(1);
  }, [searchTerm, certificates]);

  const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = String(dateStr).split('T')[0].split('-');
    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dateStr;
  };

  const closePrintModal = () => { setShowPrintModal(false); setPrintCertificateId(null); };

  return (
    <Container maxWidth={false} sx={{ width: '100%' }}>
      {/* ── Top bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 2, boxShadow: 'none', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main, marginLeft: 1.5, fontSize: 24 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: 'grey', marginRight: 1 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,1)',
                boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}` },
                '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(240,240,240,1)', boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.15)' },
                '&.Mui-focused': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,1)', boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`, '& .MuiOutlinedInput-notchedOutline': { border: `2px solid ${theme.palette.primary.main}` } },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{ width: 350, maxWidth: '100%', '& .MuiInputBase-input': { padding: '10px 14px', color: theme.palette.text.primary, fontWeight: 500 } }}
          />

          <Button
            variant="contained"
            onClick={() => setShowAddCertificateModal(true)}
            sx={{ ml: 2, width: '170px', height: '50px', borderRadius: '10px', padding: '10px 0', fontWeight: 'bold', backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.primary.dark } }}
            startIcon={<AddIcon />}
          >
            ADD CERTIFICATE
          </Button>
        </Box>

        <IconButton onClick={() => setShowUserGuideModal(true)} sx={{ width: 60, height: 60, borderRadius: '50%', minWidth: 0 }}>
          <InfoOutlineIcon sx={{ fontSize: 30 }} />
        </IconButton>
      </Box>

      {/* ── Table ── */}
      <Paper elevation={3} sx={{ paddingBottom: 0, maxWidth: '100%', boxSizing: 'border-box', mx: 'auto', backgroundColor: theme.palette.background.paper, borderRadius: '16px' }}>
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table sx={{ '& .MuiTableHead-root .MuiTableCell-root': { fontWeight: 'bold', backgroundColor: theme.palette.primary.main, color: 'white' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 70, whiteSpace: 'nowrap' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 180, whiteSpace: 'nowrap' }}>Candidate Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 200, whiteSpace: 'nowrap' }}>Certificate Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 200, whiteSpace: 'nowrap' }}>Issue Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 200, whiteSpace: 'nowrap' }}>Expiry Date</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem', minWidth: 190, whiteSpace: 'nowrap' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((certificate, index) => (
                    <TableRow
                      key={certificate.id}
                      sx={{ backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                    >
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{certificate.student_name || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{certificate.template_name || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{formatDate(certificate.issue_date)}</TableCell>
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{formatDate(certificate.expiry_date)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Print Certificate">
                          <IconButton onClick={() => handlePrint(certificate)} sx={{ color: theme.palette.success.main, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Certificate (PDF)">
                          <IconButton onClick={() => handlePrint(certificate)} sx={{ color: theme.palette.info.main, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Certificate">
                          <IconButton onClick={() => handleEdit(certificate)} sx={{ color: theme.palette.primary.light, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Certificate">
                          <IconButton color="error" onClick={() => handleDelete(certificate)} sx={{ '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{ backgroundColor: theme.palette.background.paper, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                    <TableCell colSpan={6} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {/* ── Pagination ── */}
      {filteredData.length > 0 && (
        <Box sx={{ maxWidth: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, p: 2, mx: 'auto', pt: 2 }}>
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => handleChangePage(null, page - 1)} disabled={page === 1} sx={{ color: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.action.hover }, '&.Mui-disabled': { opacity: 0.3 }, borderRadius: '50%', width: '36px', height: '36px' }}>
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent', color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary, '&:hover': { backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover } }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton onClick={() => handleChangePage(null, page + 1)} disabled={page >= Math.ceil(filteredData.length / rowsPerPage)} sx={{ color: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.action.hover }, '&.Mui-disabled': { opacity: 0.3 }, borderRadius: '50%', width: '36px', height: '36px' }}>
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select labelId="rows-per-page-label" value={rowsPerPage} onChange={handleChangeRowsPerPage} label="Rows" sx={{ height: '36px', color: theme.palette.text.primary, '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main } }}>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* ── Snackbar ── */}
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Add Modal ── */}
      <AddCertificateModal
        open={showAddCertificateModal}
        onClose={() => setShowAddCertificateModal(false)}
        onSuccess={handleSaveCertificate}
        approvedStudents={approvedStudents}
      />

      {/* ── Edit Modal ── */}
      <EditCertificateModal
        open={showEditCertificateModal}
        onClose={() => { setShowEditCertificateModal(false); setSelectedCertificateId(null); }}
        certificateId={selectedCertificateId}
        onSuccess={() => fetchData(selectedBranchId)}
        navigate={navigate}
      />

      {/* ── Certificate Print / Preview Modal ── */}
      <Modal
        open={showPrintModal}
        onClose={closePrintModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.85)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '98vw', md: 980 },
            height: { xs: '95vh', md: '88vh' },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {printCertificateId && (
            <CertificatePrintView
              certificateId={printCertificateId}
              onClose={closePrintModal}
            />
          )}
        </Box>
      </Modal>

      {/* ── Delete Confirmation ── */}
      <Modal open={showDeleteModal} onClose={handleCloseDeleteModal} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography variant="h6" sx={{ color: '#fff' }}>Delete Confirmation ⚠️</Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete the certificate for <strong>{selectedCertificate?.student_name}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">Cancel</Button>
              <Button variant="contained" color="error" onClick={handleConfirmDelete}>Yes, Delete</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ── Issue Success Modal ── */}
      <Modal open={showIssueSuccessModal} onClose={() => setShowIssueSuccessModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.85)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: { xs: '95vw', md: 620 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden', maxHeight: '90vh' }}>
          <Box sx={{ backgroundColor: theme.palette.success.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              ✅ Certificates Issued Successfully
            </Typography>
            <IconButton onClick={() => setShowIssueSuccessModal(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ p: 3, overflowY: 'auto', maxHeight: 'calc(90vh - 60px)' }}>
            {issueSuccessData && (
              <>
                <Card sx={{ mb: 2, bgcolor: theme.palette.action.hover }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Student Name</Typography>
                        <Typography variant="body2" fontWeight={600}>{issueSuccessData.student_name || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Admission Code</Typography>
                        <Typography variant="body2" fontWeight={600}>{issueSuccessData.admission_code || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Organisation</Typography>
                        <Typography variant="body2" fontWeight={600}>{issueSuccessData.organization || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Branch</Typography>
                        <Typography variant="body2" fontWeight={600}>{issueSuccessData.branch || '-'}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                  Issued Certificates ({issueSuccessData.total_certificates_issued})
                </Typography>

                {(issueSuccessData.certificates || []).map((cert, idx) => (
                  <Card key={idx} sx={{ mb: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>{cert.course_name}</Typography>
                          <Typography variant="caption" color="text.secondary">Batch: {cert.batch_name}</Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">Template: {cert.template_name}</Typography>
                          <br />
                          <Chip label={cert.certificate_no} size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                          <Tooltip title="View & Print Certificate">
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PrintIcon />}
                                onClick={() => {
                                  setShowIssueSuccessModal(false);
                                  openCertificate(cert.certificate_id);
                                }}
                                disabled={!cert.certificate_id}
                              >
                                Print
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="View & Download Certificate (PDF)">
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => {
                                  setShowIssueSuccessModal(false);
                                  openCertificate(cert.certificate_id);
                                }}
                                disabled={!cert.certificate_id}
                                color="success"
                              >
                                Download
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="outlined" onClick={() => setShowIssueSuccessModal(false)}>Close</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ── User Guide Modal ── */}
      <Modal open={showUserGuideModal} onClose={() => setShowUserGuideModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 800, maxWidth: '95vw', bgcolor: 'background.paper', borderRadius: '16px', boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>Certificate Issue Management - User Guide</Typography>
            <IconButton onClick={() => setShowUserGuideModal(false)} sx={{ color: '#fff', position: 'absolute', right: 14 }}><CloseIcon /></IconButton>
          </Box>
          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>➤ Follow these steps to issue a certificate:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click <strong>"ADD CERTIFICATE"</strong> to open the issue form.</li>
              <li>Select a <strong>Candidate Name</strong> from the dropdown.</li>
              <li>Their enrolled courses and batches will appear. Select a <strong>Certificate Template</strong> for each.</li>
              <li>Choose an <strong>Issue Date</strong> and <strong>Expiry Date</strong>.</li>
              <li>Click <strong>"SAVE"</strong>. A success dialog will appear showing all issued certificates.</li>
              <li>Use the <strong>Print</strong> or <strong>Download</strong> buttons to view the certificate immediately.</li>
            </ul>

            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}>➤ Printing or Downloading later:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Use the <strong>Print</strong> icon (🖨) in the table to open the certificate preview.</li>
              <li>Use the <strong>Download</strong> icon (⬇) to open the same preview, then click <strong>Download PDF</strong> and choose <strong>"Save as PDF"</strong> in the print dialog.</li>
            </ul>

            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}>➤ Editing a certificate issue:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>Edit</strong> icon to update the template, issue date, or expiry date.</li>
              <li>Click <strong>"UPDATE"</strong> to save changes.</li>
            </ul>

            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}>➤ Deleting a certificate issue:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>Delete</strong> icon and confirm in the dialog.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>⚠️ Note: Deleted records cannot be recovered.</Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

// ─── Add Certificate Modal ─────────────────────────────────────────────────────
const AddCertificateModal = ({ open, onClose, onSuccess, approvedStudents }) => {
  const theme = useTheme();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [certificateSelections, setCertificateSelections] = useState([]);
  const [templatesForCourses, setTemplatesForCourses] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState({});

  useEffect(() => {
    setIssueDate(todayLocal());
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedStudent(null);
      setCertificateSelections([]);
      setTemplatesForCourses({});
    }
  }, [open]);

  const handleStudentChange = (studentData) => {
    setSelectedStudent(studentData);
    if (studentData?.courses?.length > 0) {
      const pendingCourses = studentData.courses.filter((c) => !c.certificate_issued);
      const initialSelections = pendingCourses.map((course) => ({
        course_id: course.course_id,
        course_name: course.course_name,
        batch_id: course.batch_id,
        batch_name: course.batch_name,
        template_id: '',
      }));
      setCertificateSelections(initialSelections);
      const uniqueCourseIds = [...new Set(initialSelections.map((s) => s.course_id))];
      uniqueCourseIds.forEach(fetchTemplatesForCourse);
      if (pendingCourses.length === 0) {
        alert('All certificates have already been issued for this student.');
      }
    } else {
      setCertificateSelections([]);
      alert('No pending certificates available for this student.');
    }
  };

  const fetchTemplatesForCourse = async (courseId) => {
    try {
      setLoadingTemplates((prev) => ({ ...prev, [courseId]: true }));
      const response = await api.get('/admission/certificate-template/', {
        params: { course_id: courseId },
      });
      const data = response.data;
      setTemplatesForCourses((prev) => ({
        ...prev,
        [courseId]: Array.isArray(data) ? data : (data?.results || data?.data || []),
      }));
    } catch (error) {
      console.error(`Error fetching templates for course ${courseId}:`, error);
      setTemplatesForCourses((prev) => ({ ...prev, [courseId]: [] }));
    } finally {
      setLoadingTemplates((prev) => ({ ...prev, [courseId]: false }));
    }
  };

  const handleTemplateChange = (index, templateId) => {
    const updated = [...certificateSelections];
    updated[index].template_id = templateId;
    setCertificateSelections(updated);
  };

  const handleSave = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedStudent) { alert('Please select a student'); return; }
    if (!issueDate || !expiryDate) { alert('Please select issue date and expiry date'); return; }
    if (expiryDate < issueDate) { alert('Expiry date cannot be earlier than the issue date'); return; }
    const incomplete = certificateSelections.filter((s) => !s.template_id);
    if (incomplete.length > 0) { alert(`Please select certificate templates for all ${incomplete.length} course(s)`); return; }

    const payload = {
      issue_date: issueDate,
      expiry_date: expiryDate,
      certificates: certificateSelections.map((s) => ({
        course: s.course_id,
        batch: s.batch_id,
        template: parseInt(s.template_id),
      })),
    };
    onSuccess(selectedStudent.admission_id, payload);
    handleReset();
  };

  const handleReset = () => {
    setSelectedStudent(null);
    setCertificateSelections([]);
    setTemplatesForCourses({});
    setIssueDate(todayLocal());
    setExpiryDate('');
    onClose();
  };

  const totalCertificates = certificateSelections.length;
  const selectedCount = certificateSelections.filter((s) => s.template_id).length;
  const isSaveDisabled = !selectedStudent || certificateSelections.some((s) => !s.template_id) || !issueDate || !expiryDate;

  return (
    <Modal open={open} onClose={handleReset} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 800, maxWidth: '95vw', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>Add Certificate Issue</Typography>
          <IconButton onClick={handleReset} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>

        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <form onSubmit={handleSave}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="student-label" sx={{ color: theme.palette.text.primary }}>Select Candidate</InputLabel>
              <Select
                labelId="student-label"
                value={selectedStudent?.admission_id || ''}
                label="Select Candidate"
                onChange={(e) => {
                  const student = approvedStudents.find((s) => s.admission_id === e.target.value);
                  handleStudentChange(student);
                }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main } }}
              >
                {approvedStudents.length === 0 ? (
                  <MenuItem disabled value=""><em style={{ color: '#737373' }}>No Candidates available</em></MenuItem>
                ) : (
                  approvedStudents.map((student) => (
                    <MenuItem key={student.admission_id} value={student.admission_id}>
                      {student.student_name} ({student.admission_code}) - {student.pending_certificates} pending
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {selectedStudent && (
              <Card sx={{ mb: 3, bgcolor: theme.palette.background.default }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Student Information</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Admission Code</Typography>
                      <Typography variant="body2" fontWeight={500}>{selectedStudent.admission_code}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Approval Date</Typography>
                      <Typography variant="body2" fontWeight={500}>{selectedStudent.approval_date ? new Date(selectedStudent.approval_date).toLocaleDateString() : '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Total Fee</Typography>
                      <Typography variant="body2" fontWeight={500}>₹{selectedStudent.total_fee?.toLocaleString() || 0}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Pending Certificates</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ color: selectedStudent.pending_certificates > 0 ? '#ed6c02' : '#2e7d32' }}>
                        {selectedStudent.pending_certificates}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {selectedStudent?.courses?.length > 0 && (
              <Card sx={{ mb: 3, bgcolor: theme.palette.background.default }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>Enrolled Courses & Batches</Typography>
                  <Grid container spacing={2}>
                    {selectedStudent.courses.map((course, idx) => (
                      <Grid item xs={12} key={idx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', p: 1, borderRadius: 1, bgcolor: course.certificate_issued ? theme.palette.action.disabledBackground : 'transparent', opacity: course.certificate_issued ? 0.7 : 1 }}>
                          <Chip label={`Course: ${course.course_name}`} color="primary" variant="outlined" sx={{ fontWeight: 'medium' }} />
                          <Chip label={`Batch: ${course.batch_name}`} color="secondary" variant="outlined" />
                          {course.certificate_issued
                            ? <Chip label="Certificate Issued" color="success" size="small" icon={<CheckCircleOutlineIcon />} />
                            : <Chip label="Pending" color="warning" size="small" />
                          }
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {certificateSelections.length > 0 && (
              <Card sx={{ mb: 3, bgcolor: theme.palette.background.default }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>Select Certificate Templates</Typography>
                    <Typography variant="caption" color="text.secondary">Selected: {selectedCount} / {totalCertificates}</Typography>
                  </Box>
                  {certificateSelections.map((selection, index) => (
                    <Box key={index} sx={{ mb: 3, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {selection.course_name} — {selection.batch_name}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: theme.palette.text.primary }} id={`template-label-${index}`}>Select Template *</InputLabel>
                        <Select
                          labelId={`template-label-${index}`}
                          value={selection.template_id}
                          label="Select Template *"
                          onChange={(e) => handleTemplateChange(index, e.target.value)}
                          disabled={loadingTemplates[selection.course_id]}
                          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main } }}
                        >
                          {loadingTemplates[selection.course_id] ? (
                            <MenuItem disabled>Loading templates...</MenuItem>
                          ) : (templatesForCourses[selection.course_id] || []).map((template) => (
                            <MenuItem key={template.id} value={template.id}>{template.template_name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            <TextField
              label="Issue Date *" type="date" variant="outlined" fullWidth InputLabelProps={{ shrink: true }}
              value={issueDate} onChange={(e) => setIssueDate(e.target.value)}
              inputProps={{ min: todayLocal() }}
              sx={{ mb: 3, '& .MuiInputLabel-root': { color: theme.palette.text.primary }, '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main }, '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main } }}
            />
            <TextField
              label="Expiry Date *" type="date" variant="outlined" fullWidth InputLabelProps={{ shrink: true }}
              value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
              inputProps={{ min: issueDate || undefined }}
              sx={{ mb: 3, '& .MuiInputLabel-root': { color: theme.palette.text.primary }, '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main }, '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main } }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Tooltip title={isSaveDisabled ? 'Fill all required fields' : ''} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    sx={{ backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.primary.dark } }}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" color="error" onClick={handleReset}>RESET</Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

// ─── Edit Certificate Modal ────────────────────────────────────────────────────
const EditCertificateModal = ({ open, onClose, certificateId, onSuccess, navigate }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [admissionNo, setAdmissionNo] = useState('');
  const [course, setCourse] = useState(null);
  const [batch, setBatch] = useState(null);
  const [template, setTemplate] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (open && certificateId) fetchCertificateDetails();
  }, [open, certificateId]);

  useEffect(() => {
    if (course) fetchTemplatesForCourse(typeof course === 'object' ? course.id : course);
  }, [course]);

  const fetchCertificateDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admission/issue/get/${certificateId}/`);
      const data = response.data;
      setCertificateData(data);
      setStudentName(data.student_name || '');
      setAdmissionNo(data.admission_no || data.admission_code || '');
      setCourse(data.course || { id: data.course, name: data.course_name });
      setBatch(data.batch || { id: data.batch, name: data.batch_name });
      const tplId = data.template && typeof data.template === 'object' ? data.template.id : data.template;
      setTemplate(tplId != null ? String(tplId) : '');
      setIssueDate(data.issue_date ? String(data.issue_date).split('T')[0] : '');
      setExpiryDate(data.expiry_date ? String(data.expiry_date).split('T')[0] : '');
    } catch (error) {
      console.error('Error fetching certificate details:', error);
      alert('Failed to fetch certificate details');
      if (error.response?.status === 401 && navigate) {
        ['accessToken', 'access_token', 'token'].forEach((k) => localStorage.removeItem(k));
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplatesForCourse = async (courseId) => {
    try {
      setLoadingTemplates(true);
      const response = await api.get('/admission/certificate-template/', {
        params: { course_id: courseId },
      });
      const data = response.data;
      setTemplates(Array.isArray(data) ? data : (data?.results || data?.data || []));
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!template || !issueDate || !expiryDate) { alert('Please fill all required fields'); return; }
    if (expiryDate < issueDate) { alert('Expiry date cannot be earlier than the issue date'); return; }
    try {
      const payload = {
        id: certificateId,
        student: certificateData?.student,
        course: typeof course === 'object' ? course.id : course,
        batch: typeof batch === 'object' ? batch.id : batch,
        template: parseInt(template),
        issue_date: issueDate,
        expiry_date: expiryDate,
      };
      const response = await api.put(`/admission/issue/get/${certificateId}/`, payload);
      if (response.status === 200) { onSuccess(); handleCancel(); }
    } catch (error) {
      console.error('Error updating certificate:', error);
      alert(error.response?.data?.message || 'Failed to update certificate');
      if (error.response?.status === 401 && navigate) {
        ['accessToken', 'access_token', 'token'].forEach((k) => localStorage.removeItem(k));
        navigate('/login');
      }
    }
  };

  const handleCancel = () => {
    setCertificateData(null);
    setStudentName(''); setAdmissionNo('');
    setCourse(null); setBatch(null);
    setTemplate(''); setIssueDate(''); setExpiryDate('');
    setTemplates([]);
    onClose();
  };

  const isSaveDisabled = !template || !issueDate || !expiryDate;

  const fieldSx = {
    mb: 3,
    '& .MuiInputLabel-root': { color: theme.palette.text.primary },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  };

  return (
    <Modal open={open} onClose={handleCancel} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 700,
          maxWidth: '95vw',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>Edit Certificate Issue</Typography>
          <IconButton onClick={handleCancel} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>

        <CustomTableScrollbar sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1, minHeight: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>Loading certificate details...</Typography>
            </Box>
          ) : (
            <form onSubmit={handleSave}>
              <Card sx={{ mb: 3, bgcolor: theme.palette.action.hover }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>Student Information</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110 }}>Student Name:</Typography>
                      <Typography variant="body2">{studentName || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110 }}>Admission No:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{admissionNo || '-'}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mb: 3, bgcolor: theme.palette.action.hover }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>Course & Batch Information</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip label={`Course: ${certificateData?.course_name || course?.name || '-'}`} color="primary" variant="outlined" sx={{ fontWeight: 500 }} />
                    <Chip label={`Batch: ${certificateData?.batch_name || batch?.name || '-'}`} color="secondary" variant="outlined" />
                    {certificateData?.certificate_no && (
                      <Chip label={`Certificate No: ${certificateData.certificate_no}`} color="info" variant="outlined" />
                    )}
                  </Box>
                </CardContent>
              </Card>

              <FormControl fullWidth sx={fieldSx}>
                <InputLabel id="template-label">Certificate Template *</InputLabel>
                <Select
                  labelId="template-label"
                  value={template}
                  label="Certificate Template *"
                  onChange={(e) => setTemplate(e.target.value)}
                  disabled={loadingTemplates}
                >
                  {loadingTemplates ? (
                    <MenuItem disabled>Loading templates...</MenuItem>
                  ) : templates.map((t) => (
                    <MenuItem key={t.id} value={t.id.toString()}>{t.template_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Issue Date *"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                sx={fieldSx}
              />

              <TextField
                label="Expiry Date *"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                inputProps={{ min: issueDate || undefined }}
                sx={{ ...fieldSx, mb: 0 }}
              />
            </form>
          )}
        </CustomTableScrollbar>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: 2.5,
            py: 1.5,
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.paper',
          }}
        >
          <Tooltip title={isSaveDisabled ? 'Fill all required fields' : ''} arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaveDisabled || loading}
                sx={{ backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.primary.dark } }}
              >
                UPDATE
              </Button>
            </span>
          </Tooltip>
          <Button variant="contained" color="error" onClick={handleCancel}>CANCEL</Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CertificateIssue;